import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { cn } from 'support/helpers/cn/cn';
import { WebEDIRowData } from 'support/types/webediTable';
import { SuggestedContentIcon } from 'components/DataTable/cells/WebEDI/components/SuggestedContentIcon/SuggestedContentIcon';
import { shouldShowDiff } from 'components/DataTable/helpers/shouldShowDiff';
import { useValue } from 'components/WebEDI/helpers';

import { CellProps } from '../../types';
import {
  disableArrowKeys,
  isProcurosAddedValue,
  useCellClassNames,
  useIsReadonly,
  useNumberConfigurations,
} from '../cellHelpers';
import { RowStatus } from '../components/RowStatus/RowStatus';
import { InputInsideCell } from './InputInsideCell';
import { InputWrapperCell } from './InputWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> & CellProps;

export const NumberCell = ({ fieldId, column, row, table, isHighlighted, mode, diffStatus }: Props) => {
  const { t } = useTranslation();
  const {
    columnDef: { meta: columnMeta, ...columnDef },
    getIsFirstColumn,
    getIsLastColumn,
  } = column;

  const optionalConfig = columnMeta?.optionalConfig;
  const { isSubmitting = false } = table.options.meta ?? {};
  const inputValue = useValue(row.original, fieldId, optionalConfig?.expression);
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const fieldState = service.useFieldStateFromAnnotations(fieldId);
  const popoverItem = service.useItemByFieldPath(fieldId);
  const { prefix, formattedValue, suffix } = useNumberConfigurations({
    value: inputValue,
    mode,
    meta: columnMeta,
    rowData: row.original,
    fieldId,
  });

  const { register } = useFormContext();
  const isAdded = isProcurosAddedValue(row);
  const isReadOnly = useIsReadonly({
    isAdded,
    mode,
    columnMeta,
    rowIndex: row.index,
    fieldId,
    hasError: fieldState === 'error',
  });
  const disabled = isSubmitting;
  const hasDiff = shouldShowDiff(diffStatus, fieldState);
  const cellClassNames = useCellClassNames({
    isLastColumn: getIsLastColumn(),
    isFirstColumn: getIsFirstColumn(),
    row,
    table,
  });

  const inputClassNames = cn(
    {
      'group-hover:bg-red-100 bg-red-50 focus:bg-transparent! disabled:bg-red-50!': fieldState === 'error',
      'group-hover:bg-blue-100 bg-blue-50 focus:bg-transparent! disabled:bg-blue-50! text-blue-700 focus:text-inherit focus-visible:ring-blue-500! focus-visible:border-blue-500!':
        fieldState === 'pending-validation',
      'group-hover:bg-procuros-green-50 focus:bg-transparent! disabled:bg-transparent!': fieldState === 'default',
      'bg-warning-50': fieldState === 'warning',
    },
    cellClassNames,
    'text-end',
    optionalConfig?.isStatusColumn ? 'pl-10!' : undefined,
  );

  return (
    <InputWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      fieldState={fieldState}
      diffStatus={diffStatus}
    >
      <InputInsideCell
        className={cn(
          'size-full h-full rounded-none border-none! bg-transparent p-3 text-xs shadow-none! outline-hidden!',
          inputClassNames,
        )}
        placeholder={
          !formattedValue && popoverItem?.type === 'required' ? t('common:table.errors.requiredField') : undefined
        }
        title={formattedValue ? String(formattedValue) : undefined}
        readOnly={isReadOnly}
        disabled={disabled}
        type="number"
        {...register(fieldId, { valueAsNumber: true })}
        value={optionalConfig?.expression ? inputValue : undefined}
        fieldState={fieldState}
        leftIcon={
          optionalConfig?.isStatusColumn ? (
            <RowStatus
              statusColumn={optionalConfig.quantityField}
              currentPath={fieldId}
              isProcurosAdded={isAdded}
              currentColumn={columnDef && 'accessorKey' in columnDef ? (columnDef.accessorKey as string) : ''}
              tooltips={{
                deleted: t('webedi:inputCell.status.deleted'),
                partial: t('webedi:inputCell.status.partial'),
                complete: t('webedi:inputCell.status.completed'),
              }}
            />
          ) : null
        }
        rightAddon={
          suffix || hasDiff ? (
            <>
              {suffix}
              {hasDiff ? <SuggestedContentIcon mode="edit" /> : null}
            </>
          ) : null
        }
        leftAddon={prefix}
        step={0.01}
        onKeyDown={disableArrowKeys}
        id={fieldId}
        data-field={fieldId}
        data-testid={fieldId}
        data-1p-ignore
      />
    </InputWrapperCell>
  );
};
